import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GoldButton, OutlineButton, EmptyState, ui } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PropertyCard } from "@/components/property/PropertyCard";
import { TField, TSelect } from "@/components/property/forms";
import { Building2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Properties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ occupancy: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nickname: "", address: "", purchase_date: "", purchase_price: "", estimated_market_value: "", occupancy_status: "Tenanted" });

  const load = () => api.get("/properties").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => {
    load();
    api.get("/properties/meta/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.nickname) return toast.error("Give the property a nickname");
    setSaving(true);
    try {
      const { data } = await api.post("/properties", {
        ...form, purchase_price: Number(form.purchase_price) || 0,
        estimated_market_value: Number(form.estimated_market_value) || 0,
      });
      toast.success("Property created"); setOpen(false);
      navigate(`/properties/${data.id}`);
    } catch { toast.error("Could not create property"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Properties" subtitle="Each property is its own permanent financial record — acquisition through to disposal."
        action={<GoldButton data-testid="add-property-btn" onClick={() => setOpen(true)}><Plus size={16} /> Add property</GoldButton>} />

      {loading ? (
        <div className="text-[#8A7F70] text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Building2} title="No properties yet"
          hint="Add your first property to open its dedicated Financial Operating System — financials, mortgage, insurance, maintenance, documents and calendar."
          action={<GoldButton data-testid="add-first-property" onClick={() => setOpen(true)}><Plus size={16} /> Add property</GoldButton>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="property-grid">
          {items.map((p) => <PropertyCard key={p.id} property={p} currency={cur} onOpen={() => navigate(`/properties/${p.id}`)} />)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${ui.dialog} max-w-2xl`}>
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Add a property</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="col-span-2"><TField label="Nickname" value={form.nickname} onChange={(v) => setForm({ ...form, nickname: v })} placeholder="e.g. Bondi Apartment" testid="prop-nickname" /></div>
            <div className="col-span-2"><TField label="Full address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="prop-address" /></div>
            <TField label="Purchase date" type="date" value={form.purchase_date} onChange={(v) => setForm({ ...form, purchase_date: v })} testid="prop-purchase-date" />
            <TField label="Purchase price" type="number" value={form.purchase_price} onChange={(v) => setForm({ ...form, purchase_price: v })} testid="prop-purchase-price" />
            <TField label="Estimated market value" type="number" value={form.estimated_market_value} onChange={(v) => setForm({ ...form, estimated_market_value: v })} testid="prop-market-value" />
            <TSelect label="Occupancy status" value={form.occupancy_status} options={options.occupancy || ["Tenanted"]} onChange={(v) => setForm({ ...form, occupancy_status: v })} testid="prop-occupancy" />
          </div>
          <DialogFooter>
            <OutlineButton onClick={() => setOpen(false)}>Cancel</OutlineButton>
            <GoldButton data-testid="prop-create" onClick={create} disabled={saving}>{saving ? <Loader2 size={15} className="animate-spin" /> : null} Create property</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
